import { useCallback, useMemo, useState, type MouseEvent } from 'react';
import { useTranslations } from 'next-intl';
import type {
  AssetPreviewDto,
  AssetsSearchGroup,
} from '@terra/shared-contracts-assets';
import { useBottomPanel, useMainContentTabs } from '../../../store/layoutStore';
import type { OpenMainContentTabInput } from '../../../store/layoutStore';
import { useGlobalContextMenu } from '../../../hooks/useGlobalContextMenu';
import { GlobalMenuItemsTypeEnum } from '../../../store/globalContextMenuStore';
import { useAssetsTableSelection } from '../../../hooks/useAssetsTableSelection';
import {
  buildAssetsTableRows,
  getNewAssetIds,
  isAssetsTableGroupHeaderRow,
  resolveAssetsTableLoadMoreRowIndex,
} from './assets-table.model';
import { getAssetTabInput } from './asset-tab-inputs';
import type {
  AssetsTableContextValue,
  AssetsTableScreenshotRequest,
} from './AssetsTableContext';
import type { AssetsTableView } from './useAssetsTableView';

export function useAssetsTableModel({
  view,
  assets,
  groups,
  applicationId,
  lastVisitTimestamp,
  selectionScopeKey,
}: {
  view: AssetsTableView;
  assets: AssetPreviewDto[];
  groups: AssetsSearchGroup[] | undefined;
  applicationId: string | null;
  lastVisitTimestamp: Date | null | undefined;
  selectionScopeKey: string;
}) {
  const t = useTranslations('proxy.assets.table');
  const bottomPanel = useBottomPanel();
  const { navigateInActiveTab } = useMainContentTabs();
  const { openContextMenu } = useGlobalContextMenu();
  const [screenshotRequest, setScreenshotRequest] =
    useState<AssetsTableScreenshotRequest | null>(null);
  const selection = useAssetsTableSelection({ assets, selectionScopeKey });
  const { collapsedGroupKeys, setCollapsedGroupKeys, toggleGroupCollapsed } =
    view;
  const rows = useMemo(
    () =>
      buildAssetsTableRows({
        assets,
        groupBy: view.groupBy,
        serverGroups: groups,
        collapsedGroupKeys,
        emptyGroupLabel: t('emptyGroup'),
      }),
    [assets, collapsedGroupKeys, groups, t, view.groupBy],
  );
  const loadMoreRowIndex = useMemo(
    () =>
      resolveAssetsTableLoadMoreRowIndex({
        rows,
        isGroupedOnServer: Boolean(view.groupBy && groups),
        collapsedGroupKeys,
      }),
    [collapsedGroupKeys, groups, rows, view.groupBy],
  );
  const groupKeys = useMemo(
    () => rows.filter(isAssetsTableGroupHeaderRow).map((row) => row.groupKey),
    [rows],
  );
  const areAllGroupsCollapsed =
    groupKeys.length > 0 &&
    groupKeys.every((groupKey) => collapsedGroupKeys.has(groupKey));
  const expandAllGroups = useCallback(
    () => setCollapsedGroupKeys([]),
    [setCollapsedGroupKeys],
  );
  const collapseAllGroups = useCallback(
    () => setCollapsedGroupKeys(groupKeys),
    [groupKeys, setCollapsedGroupKeys],
  );
  const openTabContextMenu = useCallback(
    ({
      event,
      tab,
    }: {
      event: MouseEvent<HTMLElement> | globalThis.MouseEvent;
      tab: OpenMainContentTabInput;
    }) =>
      openContextMenu({
        event,
        items: [{ type: GlobalMenuItemsTypeEnum.OPEN_IN_NEW_TAB, tab }],
      }),
    [openContextMenu],
  );
  const openAsset = useCallback(
    (asset: AssetPreviewDto) => {
      if (applicationId) {
        navigateInActiveTab(getAssetTabInput({ asset, applicationId }));
      }
    },
    [applicationId, navigateInActiveTab],
  );
  const openAssetContextMenu = useCallback(
    ({
      event,
      asset,
    }: {
      event: globalThis.MouseEvent;
      asset: AssetPreviewDto;
    }) => {
      if (applicationId) {
        openTabContextMenu({
          event,
          tab: getAssetTabInput({ asset, applicationId }),
        });
      }
    },
    [applicationId, openTabContextMenu],
  );
  const viewGraph = useCallback(
    (endpointId: string) => {
      bottomPanel.setCurrentEndpointId(endpointId);
      bottomPanel.setTab('code-graph');
      bottomPanel.open();
    },
    [bottomPanel],
  );
  const loadedAssetIds = useMemo(
    () => assets.map((asset) => asset.id),
    [assets],
  );
  const newAssetIds = useMemo(
    () => getNewAssetIds({ assets, lastVisitTimestamp }),
    [assets, lastVisitTimestamp],
  );
  const contextValue = useMemo<AssetsTableContextValue>(
    () => ({
      selectedAssetIds: selection.selectedAssetIds,
      loadedAssetIds,
      collapsedGroupKeys,
      newAssetIds,
      onSetAssetIdsSelected: selection.setAssetIdsSelected,
      onSelectAllLoaded: selection.selectAllLoaded,
      onToggleGroupCollapsed: toggleGroupCollapsed,
      onOpenTab: navigateInActiveTab,
      onOpenTabContextMenu: openTabContextMenu,
      onViewGraph: viewGraph,
      onOpenScreenshots: setScreenshotRequest,
    }),
    [
      collapsedGroupKeys,
      loadedAssetIds,
      navigateInActiveTab,
      newAssetIds,
      openTabContextMenu,
      selection.selectAllLoaded,
      selection.selectedAssetIds,
      selection.setAssetIdsSelected,
      toggleGroupCollapsed,
      viewGraph,
    ],
  );

  return {
    rows,
    loadMoreRowIndex,
    selection,
    contextValue,
    screenshotRequest,
    closeScreenshots: () => setScreenshotRequest(null),
    areAllGroupsCollapsed,
    expandAllGroups,
    collapseAllGroups,
    openAsset,
    openAssetContextMenu,
  };
}
